/**
 * Configuration for states that offer multiple course types.
 *
 * Most states have a single curriculum and need no course_type.
 * States listed here have two or more courses that map to
 * different data sets (stateLMapping, teacherGuideLMapping, etc.).
 *
 * The `value` is stored in districts.course_type in the database.
 * The `stateMappingName` is the key used in stateLMappings / exports.
 */

export interface CourseTypeOption {
  value: string;
  label: string;
  /** The key used in stateLMappings (e.g., "Texas PFLE"). Null means use the base state name. */
  stateMappingName: string | null;
}

export const STATE_COURSE_TYPES: Record<string, CourseTypeOption[]> = {
  Texas: [
    {
      value: "pfl",
      label: "Personal Financial Literacy (HB 27)",
      stateMappingName: null,
    },
    {
      value: "pfle",
      label: "Personal Financial Literacy & Economics (\u00A7113.76)",
      stateMappingName: "Texas PFLE",
    },
  ],
};

/**
 * Returns the course type options for a state, or null if the state
 * has only one course (no selector needed).
 */
export function getCourseTypesForState(
  stateName: string
): CourseTypeOption[] | null {
  const options = STATE_COURSE_TYPES[stateName];
  return options && options.length > 1 ? options : null;
}

/**
 * Resolves the effective state name used for data lookups.
 *
 * If course_type is null or the state has no multi-course config,
 * returns the base state name as-is.
 *
 * Example: resolveStateName("Texas", "pfle") => "Texas PFLE"
 *          resolveStateName("Texas", "pfl")  => "Texas"
 *          resolveStateName("Texas", null)    => "Texas"
 *          resolveStateName("Oklahoma", null) => "Oklahoma"
 */
export function resolveStateName(
  baseStateName: string,
  courseType: string | null | undefined
): string {
  if (!courseType) return baseStateName;

  const options = STATE_COURSE_TYPES[baseStateName];
  if (!options) return baseStateName;

  const match = options.find((o) => o.value === courseType);
  if (!match) return baseStateName;

  return match.stateMappingName ?? baseStateName;
}
